/**
 * IndexedDB cache of PDF source bytes so reopen works even when
 * Firebase Storage downloads are blocked by browser CORS.
 */

const DB_NAME = 'craftara-pdf-sources';
const STORE = 'sources';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });
}

export async function idbPutPdfSource(docId: string, bytes: ArrayBuffer): Promise<void> {
  if (!docId || !bytes || bytes.byteLength === 0) return;
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(new Uint8Array(bytes));
  const blob = new Blob([copy], { type: 'application/pdf' });

  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, docId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB put failed'));
    });
  } finally {
    db.close();
  }
}

export async function idbGetPdfSource(docId: string): Promise<ArrayBuffer | null> {
  if (!docId) return null;
  const db = await openDb();
  try {
    const blob = await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(docId);
      req.onsuccess = () => {
        const val = req.result;
        resolve(val instanceof Blob ? val : null);
      };
      req.onerror = () => reject(req.error || new Error('IndexedDB get failed'));
    });
    if (!blob || blob.size === 0) return null;
    return blob.arrayBuffer();
  } finally {
    db.close();
  }
}

export async function idbDeletePdfSource(docId: string): Promise<void> {
  if (!docId) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(docId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB delete failed'));
    });
    db.close();
  } catch {
    /* ignore */
  }
}

export function pickPdfFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,.pdf';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}
